import { Bell, Bot, CheckCheck, MessageSquare, MessagesSquare, UserPlus } from 'lucide-react';
import { useState, type ComponentType } from 'react';
import type { CrmNotification, CrmNotificationType } from '../../api/types';
import { CHANNEL_TYPE_ICONS, CHANNEL_TYPE_META } from '../../lib/commsMeta';
import { useTimeFormat } from '../../lib/tenantTime';
import {
  notificationContact,
  notificationOrigin,
  notificationTitle,
  useNotifications,
} from './notifications';

const TYPE_ICONS: Record<CrmNotificationType, ComponentType<{ size?: number | string }>> = {
  conversation_assigned: UserPlus,
  new_message: MessageSquare,
  bot_handoff: Bot,
  conversation_created: MessagesSquare,
};

const TYPE_TONES: Record<CrmNotificationType, { bg: string; fg: string }> = {
  conversation_assigned: { bg: 'var(--c-blue-muted)', fg: 'var(--c-blue)' },
  new_message: { bg: 'var(--c-primary-muted)', fg: 'var(--c-primary)' },
  bot_handoff: { bg: 'var(--c-orange-muted)', fg: 'var(--c-orange)' },
  conversation_created: { bg: 'var(--c-green-muted)', fg: 'var(--c-green)' },
};

function NotificationRow({
  notification,
  onOpen,
}: {
  notification: CrmNotification;
  onOpen: (n: CrmNotification) => void;
}) {
  const { listTime } = useTimeFormat();
  const Icon = TYPE_ICONS[notification.type] ?? MessageSquare;
  const tone = TYPE_TONES[notification.type] ?? TYPE_TONES.new_message;
  // WHAT happened is the tile; WHERE it happened is the mark on its corner —
  // the same pairing the conversation list uses over a contact avatar. Both are
  // needed: a handoff and a new message look nothing alike to an agent, and
  // neither does an email and a WhatsApp.
  const channelType = notification.snapshot.channelType ?? '';
  const ChannelMark = CHANNEL_TYPE_ICONS[channelType];
  const channelTone = CHANNEL_TYPE_META[channelType];
  return (
    <button
      type="button"
      className={`nrow${notification.readAt ? '' : ' unread'}`}
      onClick={() => onOpen(notification)}
    >
      <span className="nrow-icon" style={{ background: tone.bg, color: tone.fg }}>
        <Icon size={14} />
        {ChannelMark && channelTone && (
          <span className="nrow-chdot" style={{ background: channelTone.muted }}>
            <ChannelMark size={10} style={{ color: channelTone.color }} />
          </span>
        )}
      </span>
      <span className="nrow-main">
        <span className="nrow-toprow">
          <span className="nrow-title">{notificationTitle(notification)}</span>
          <span className="nrow-time">{listTime(notification.createdAt)}</span>
        </span>
        {notification.snapshot.preview && (
          <span className="nrow-preview">{notification.snapshot.preview}</span>
        )}
        <span className="nrow-meta">
          {[notificationContact(notification), notificationOrigin(notification)]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
      {!notification.readAt && <span className="nrow-dot" />}
    </button>
  );
}

/** The bell in the tab strip: unread badge + the notifications panel. */
export function NotificationsBell() {
  const {
    unread,
    items,
    loading,
    hasMore,
    loadingMore,
    loadMore,
    denied,
    hostedByDashboard,
    markAllRead,
    openNotification,
  } = useNotifications();
  const [open, setOpen] = useState(false);

  // The whole feature needs crm_all; without it the API answers 1036 and the
  // bell would only ever be an empty shell — hide it. Framed, the dashboard's
  // own bell lists these under CRM, one header above this one.
  if (denied || hostedByDashboard) return null;

  return (
    <div className="nbell-host">
      <button
        type="button"
        className={`comms-tab-action nbell${open ? ' open' : ''}`}
        title="Notifications"
        onClick={() => setOpen((current) => !current)}
      >
        <Bell size={15} />
        {unread > 0 && <span className="nbell-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <>
          <button
            type="button"
            className="npanel-backdrop"
            aria-label="Close notifications"
            onClick={() => setOpen(false)}
          />
          <div className="npanel">
            <div className="npanel-head">
              <span className="npanel-title">Notifications</span>
              {unread > 0 && <span className="npanel-count">{unread} unread</span>}
              <div style={{ flex: 1 }} />
              <button
                type="button"
                className="npanel-readall"
                disabled={unread === 0}
                onClick={() => markAllRead()}
              >
                <CheckCheck size={13} />
                Mark all read
              </button>
            </div>
            <div className="npanel-list">
              {items.map((notification) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  onOpen={(n) => {
                    setOpen(false);
                    openNotification(n);
                  }}
                />
              ))}
              {items.length === 0 && loading && (
                <div className="npanel-empty" aria-busy>
                  Loading notifications…
                </div>
              )}
              {items.length === 0 && !loading && (
                <div className="npanel-empty">
                  Nothing yet — assignments, new messages and bot handoffs land here.
                </div>
              )}
              {hasMore && (
                <button
                  type="button"
                  className="npanel-more"
                  disabled={loadingMore}
                  onClick={() => loadMore()}
                >
                  {loadingMore && <span className="btn-spin" />}
                  {loadingMore ? 'Loading…' : 'Load older'}
                </button>
              )}
            </div>
            <div className="npanel-foot">
              Tune what notifies you under <b>Settings → Notifications</b>.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
